import { HelpCircle } from 'lucide-react';
import { Radio as RACRadio, type RadioProps as RACRadioProps, composeRenderProps } from 'react-aria-components';
import { tv, type VariantProps } from 'tailwind-variants';
import { cn, focusRing } from '~/shared/components/utils';
import { IconButtonPopover } from '~/shared/IconButtonPopover';

const radioStyles = tv({
	slots: {
		base: 'group flex gap-4 text-gray-800 transition disabled:text-gray-300 forced-colors:disabled:text-[GrayText]',
		dot: 'rounded-full border-gray-400 bg-white transition-all group-pressed:border-gray-500',
		label: 'flex items-center gap-2'
	},
	variants: {
		size: {
			small: {
				base: 'items-center p-0 text-xs',
				dot: 'ml-1 min-h-4 min-w-4'
			},
			medium: {
				base: 'min-h-[50px] items-start p-2 text-sm',
				dot: 'mt-1 min-h-5 min-w-5'
			}
		},
		labelPrefix: {
			true: {
				base: 'group flex items-start'
			}
		},
		isSelected: {
			true: {
				dot: 'border-[7px] border-gray-700 group-pressed:border-gray-800 forced-colors:!border-[Highlight]'
			},
			false: {
				dot: 'border-2'
			}
		},
		isInvalid: {
			true: {
				dot: 'border-red-700 group-pressed:border-red-800 forced-colors:!border-[Mark]'
			}
		},
		isDisabled: {
			true: {
				dot: 'border-gray-200 forced-colors:!border-[GrayText]'
			}
		},
		isSuggested: {
			true: {
				base: 'bg-primary-500/5'
			}
		},
		isAddendumOriginal: {
			true: {
				base: 'bg-blue-500/10',
				dot: 'mt-1.5'
			},
			false: {
				label: 'mt-0.5'
			}
		},
		isAddendumUpdated: {
			true: {
				base: 'bg-yellow-500/10'
			}
		}
	},
	defaultVariants: {
		size: 'medium'
	}
});

type RadioStyleVariants = Omit<VariantProps<typeof radioStyles>, 'labelPrefix'>;

export interface RadioProps extends Omit<RACRadioProps, 'children'>, RadioStyleVariants {
	label: string;
	labelPrefix?: string;
	description?: string;
	suggestionText?: string;
	isAddendumOriginal?: boolean;
	isAddendumUpdated?: boolean;
}

export function Radio({ label, size, labelPrefix, description, suggestionText, ...props }: RadioProps) {
	const {
		base,
		dot,
		label: labelStyles
	} = radioStyles({
		size,
		labelPrefix: !!labelPrefix,
		isInvalid: props.isInvalid,
		isDisabled: props.isDisabled,
		isSelected: props.isSelected,
		isSuggested: props.isSuggested,
		isAddendumOriginal: props.isAddendumOriginal,
		isAddendumUpdated: props.isAddendumUpdated
	});
	return (
		<RACRadio {...props} className={composeRenderProps(props.className, className => cn(base(), className))}>
			{renderProps => (
				<>
					<div className={cn(dot(renderProps), focusRing(renderProps))} />
					<div className={labelStyles(renderProps)}>
						{!description ? (
							<RadioText label={label} labelPrefix={labelPrefix} />
						) : (
							<div className="flex flex-col gap-1">
								<RadioText label={label} labelPrefix={labelPrefix} />
								<span className="text-xs text-gray-500">{description}</span>
							</div>
						)}
						{suggestionText && (
							<IconButtonPopover
								Icon={HelpCircle}
								aria-label="Show suggested answer reason"
								popoverText={suggestionText}
								size="sm"
								appearance="ghost"
							/>
						)}
						{props.isAddendumOriginal && (
							<IconButtonPopover
								Icon={HelpCircle}
								aria-label="Marked as original answer"
								popoverText="Answer was marked by the original author of the OASIS"
								size="sm"
								appearance="ghost"
							/>
						)}
					</div>
				</>
			)}
		</RACRadio>
	);
}

function RadioText({ label, labelPrefix }: { label: string; labelPrefix?: string }) {
	if (!labelPrefix) {
		return <span dangerouslySetInnerHTML={{ __html: label }} />;
	}
	return (
		<div className="flex gap-1">
			<span className="shrink-0">{labelPrefix}</span>
			<span dangerouslySetInnerHTML={{ __html: label }} />
		</div>
	);
}
